'use client'

import { AlertTriangle, Trash2, Clock } from 'lucide-react'
import { DateTime } from 'luxon'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

import { OverlapDetails } from '../../utils/priorityCalculator'

interface EditOverlapConfirmationModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	overlapDetails: OverlapDetails | null
	editingEventTitle: string
	onConfirmUpdate: () => void
	onConfirmDelete: () => void
	onCancel: () => void
}

function formatTime(date: Date): string {
	return DateTime.fromJSDate(date).toLocaleString(DateTime.TIME_SIMPLE)
}

export const EditOverlapConfirmationModal = ({
	open,
	onOpenChange,
	overlapDetails,
	editingEventTitle,
	onConfirmUpdate,
	onConfirmDelete,
	onCancel,
}: EditOverlapConfirmationModalProps) => {
	const { t } = useTranslation()
	if (!overlapDetails) return null

	const {
		existingEvent,
		existingStart,
		existingEnd,
		newStart,
		newEnd,
		scenario,
	} = overlapDetails

	// Determine if the overlapped slot will be completely covered
	const willCompletelyOverlap = scenario === 'new_covers_existing'

	// Calculate how the affected slot will be modified
	const getAffectedSlotNewTimes = () => {
		if (scenario === 'new_inside_existing') {
			// New slot is completely inside existing - move existing's start to after new ends
			return {
				newStart: newEnd,
				newEnd: existingEnd,
			}
		} else if (scenario === 'new_overlaps_end') {
			// New slot overlaps the end of existing - move existing's end to before new starts
			return {
				newStart: existingStart,
				newEnd: newStart,
			}
		} else if (scenario === 'new_overlaps_start') {
			// New slot overlaps the start of existing - move existing's start to after new ends
			return {
				newStart: newEnd,
				newEnd: existingEnd,
			}
		}
		return null
	}

	const affectedSlotNewTimes = getAffectedSlotNewTimes()

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg p-0 gap-0">
				<DialogHeader className="px-6 pt-6 pb-4">
					<div className="flex items-center gap-3">
						<div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
							<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
						</div>
						<div>
							<DialogTitle className="text-lg">
								{willCompletelyOverlap
									? t('entities.schedule.overlap.timeslot_will_be_replaced')
									: t('entities.schedule.overlap.timeslot_overlap_detected')}
							</DialogTitle>
							<DialogDescription className="text-sm mt-1">
								{willCompletelyOverlap
									? t('entities.schedule.overlap.changes_completely_cover')
									: t('entities.schedule.overlap.changes_overlap')}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<Separator />

				<div className="px-6 py-5 space-y-4">
					{/* Editing Slot Info */}
					<div className="space-y-2">
						<h4 className="text-sm font-medium text-muted-foreground">
							{t('entities.schedule.overlap.your_changes')}
						</h4>
						<div className="p-3 rounded-lg border bg-primary/5 border-primary/20">
							<div className="flex items-center gap-2">
								<span className="font-medium text-sm">{editingEventTitle}</span>
							</div>
							<div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
								<Clock className="w-3 h-3" />
								<span>
									{formatTime(newStart)} - {formatTime(newEnd)}
								</span>
							</div>
						</div>
					</div>

					{/* Affected Slot Info */}
					<div className="space-y-2">
						<h4 className="text-sm font-medium text-muted-foreground">
							{t('entities.schedule.overlap.affected_timeslot')}
						</h4>
						<div className="p-3 rounded-lg border bg-muted/30">
							<div className="flex items-center gap-2">
								<div
									className="w-3 h-3 rounded-full"
									style={{
										backgroundColor:
											existingEvent.backgroundColor || existingEvent.color,
									}}
								/>
								<span className="font-medium text-sm">
									{existingEvent.title}
								</span>
							</div>
							<div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
								<Clock className="w-3 h-3" />
								<span>
									{formatTime(existingStart)} - {formatTime(existingEnd)}
								</span>
							</div>
						</div>
					</div>

					{/* What will happen */}
					<div className="space-y-2">
						<h4 className="text-sm font-medium text-muted-foreground">
							{t('entities.schedule.overlap.what_will_happen')}
						</h4>
						{willCompletelyOverlap ? (
							<div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20">
								<p className="text-sm text-red-700 dark:text-red-400">
									{t('entities.schedule.overlap.slot_completely_covered', {
										title: existingEvent.title,
									})}
								</p>
							</div>
						) : (
							<div className="p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20">
								<p className="text-sm text-amber-700 dark:text-amber-400">
									{t('entities.schedule.overlap.slot_will_be_updated', {
										title: existingEvent.title,
										times: affectedSlotNewTimes
											? `${formatTime(affectedSlotNewTimes.newStart)} - ${formatTime(affectedSlotNewTimes.newEnd)}`
											: t('entities.schedule.overlap.avoid_overlap'),
									})}
								</p>
							</div>
						)}
					</div>
				</div>

				<Separator />

				<DialogFooter className="px-6 py-4 gap-2">
					<Button
						type="button"
						variant="outline"
						onClick={onCancel}
						className="h-9 rounded-full px-6"
					>
						{t('actions.back')}
					</Button>
					{willCompletelyOverlap ? (
						<Button
							type="button"
							variant="destructive"
							onClick={onConfirmDelete}
							className="h-9 rounded-full px-6 gap-2"
						>
							<Trash2 className="w-4 h-4" />
							{t('entities.schedule.overlap.delete_and_continue')}
						</Button>
					) : (
						<Button
							type="button"
							onClick={onConfirmUpdate}
							className="h-9 rounded-full px-6"
						>
							{t('entities.schedule.overlap.update_and_continue')}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
